import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import {
  MarketplaceApiService,
  MiCita,
  MiRecomendacion,
  MovimientoPunto,
  Premio,
} from '../services/marketplace-api.service';
import { MarketplaceAuthService } from '../services/marketplace-auth.service';
import { AuthModalService } from '../services/auth-modal.service';

/**
 * Perfil del cliente ("Mi cuenta"). Vive dentro del shell del marketplace y usa la auth
 * del marketplace (MktUsuario). Muestra datos personales, "Mis Citas" (marketplace +
 * clínica) y "Recomendaciones de tu especialista". Ver SPEC-026 (Fases 2-3).
 *
 * NO usa el perfil-cliente legacy de PagesModule (auth C#).
 */
@Component({
  selector: 'app-mi-cuenta',
  standalone: true,
  imports: [CommonModule, RouterLink],
  styles: [`
    :host{ display:block; background:#f6f8fb; min-height:100vh; }
    .top{ background:linear-gradient(135deg,#00A7DB,#0f6ea8); color:#fff; padding:24px 20px 44px; }
    .wrap{ max-width:900px; margin:0 auto; padding:0 18px; }
    .back{ color:#fff; opacity:.9; text-decoration:none; font-size:14px; display:inline-block; margin-bottom:12px; }
    .top h1{ margin:0; font-size:1.45rem; font-weight:800; }
    .top .sub{ opacity:.92; font-size:14px; margin-top:4px; }
    .content{ max-width:900px; margin:-26px auto 60px; padding:0 18px; }
    .card{ background:#fff; border:1px solid #eef2f7; border-radius:14px; box-shadow:0 6px 18px rgba(9,50,80,.07); overflow:hidden; margin-bottom:18px; }
    .card > h2{ margin:0; font-size:1.05rem; color:#12263a; padding:16px 18px; border-bottom:1px solid #f1f4f8; }
    .me{ padding:16px 18px; }
    .me .name{ font-size:1.2rem; font-weight:800; color:#12263a; }
    .me .email{ font-size:13.5px; color:#5b6b7b; margin-top:2px; }
    .row{ display:flex; align-items:flex-start; gap:14px; padding:14px 18px; border-bottom:1px solid #f1f4f8; flex-wrap:wrap; }
    .row:last-child{ border-bottom:0; }
    .row .info{ flex:1; min-width:200px; }
    .row .info h3{ margin:0; font-size:1rem; color:#12263a; }
    .biz{ display:flex; align-items:center; gap:8px; flex-wrap:wrap; margin-bottom:3px; }
    .chip.rubro{ background:#eafaf1; color:#1a7f47; text-transform:uppercase; }
    .chip.origen{ background:#eef2f7; color:#48586a; }
    .puntos .saldo{ padding:14px 18px 6px; }
    .puntos .saldo .val{ font-size:2rem; font-weight:800; color:#0f6ea8; letter-spacing:-.5px; }
    .puntos .saldo .unit{ font-size:1rem; font-weight:700; color:#8a97a8; }
    .pts{ font-size:1.05rem; font-weight:800; }
    .pts.pos{ color:#1a7f47; }
    .pts.neg{ color:#c0392b; }
    .row .info .d{ font-size:13px; color:#5b6b7b; }
    .row .info .when{ font-size:12.5px; color:#8a97a8; margin-top:3px; }
    .right{ text-align:right; display:flex; flex-direction:column; align-items:flex-end; gap:6px; }
    .chip{ font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.5px; border-radius:20px; padding:3px 10px; }
    .chip.marketplace{ background:#eef6fb; color:#0f6ea8; }
    .chip.clinica{ background:#f3eefb; color:#6b3fa8; }
    .estado{ font-size:12.5px; color:#48586a; font-weight:600; }
    .quote{ font-size:13.5px; color:#33475b; background:#f7fafc; border-left:3px solid #00A7DB; border-radius:0 8px 8px 0; padding:8px 12px; margin:2px 0; }
    .quote.doc{ border-left-color:#8a97a8; color:#5b6b7b; }
    .loading, .empty{ text-align:center; color:#8a97a8; padding:34px 18px; font-size:14px; }
    .gate{ max-width:520px; margin:60px auto; text-align:center; padding:0 18px; }
    .gate .card{ padding:34px 24px; }
    .gate h2{ border:0; padding:0; margin:0 0 8px; font-size:1.25rem; color:#12263a; }
    .gate p{ color:#5b6b7b; font-size:14px; margin:0 0 20px; }
    .btn{ background:#00A7DB; color:#fff; border:0; border-radius:10px; padding:12px 22px; font-weight:700; cursor:pointer; font-size:15px; }
    .btn:hover{ filter:brightness(.95); }
    .canjear{ background:#00A7DB; color:#fff; border:0; border-radius:999px; padding:9px 16px; font-weight:700; cursor:pointer; font-size:13px; white-space:nowrap; }
    .canjear:hover:not(:disabled){ filter:brightness(.95); }
    .canjear:disabled{ background:#e5e9ef; color:#98a6b5; cursor:not-allowed; }
    .canje-ok{ background:#eafaf1; color:#1a7f47; border:1px solid #b7e6c9; border-radius:10px; padding:10px 14px; margin:14px 18px; font-size:13.5px; }
    .canje-err{ background:#fdecea; color:#c0392b; border:1px solid #f5c6c2; border-radius:10px; padding:10px 14px; margin:14px 18px; font-size:13.5px; }
    .cols{ display:block; }

    /* ---- Tablet/desktop: citas + recomendaciones en 2 columnas ---- */
    @media (min-width:900px){
      .content{ max-width:960px; }
      .cols{ display:grid; grid-template-columns:1fr 1fr; gap:18px; align-items:start; }
      .cols .card{ margin-bottom:0; }
    }
  `],
  template: `
  @if (auth.isLogged()) {
    <div class="top">
      <div class="wrap">
        <a class="back" [routerLink]="['/marketplace']">← Volver al marketplace</a>
        <h1>Mi cuenta</h1>
        <div class="sub">Tus citas y las recomendaciones de tu especialista.</div>
      </div>
    </div>

    <div class="content">
      <!-- Datos personales -->
      <div class="card">
        <div class="me">
          <div class="name">{{ auth.user()?.nombre || 'Cliente' }}</div>
          <div class="email">{{ auth.user()?.email }}</div>
        </div>
      </div>

      <!-- Mis Puntos -->
      <div class="card puntos">
        <h2>Mis Puntos</h2>
        @if (cargandoPuntos) {
          <div class="loading">Cargando tus puntos…</div>
        } @else {
          <div class="saldo">
            <span class="val">{{ formatPts(saldo) }}</span> <span class="unit">pts</span>
          </div>
          @if (movimientos.length) {
            @for (m of movimientos; track $index) {
              <div class="row">
                <div class="info">
                  <div class="biz">
                    <span class="chip origen">{{ m.origen }}</span>
                    @if (m.concepto) { <h3>{{ m.concepto }}</h3> }
                  </div>
                  <div class="when">📅 {{ m.fecha }}</div>
                </div>
                <div class="right">
                  <span class="pts" [class.pos]="m.puntos >= 0" [class.neg]="m.puntos < 0">
                    {{ m.puntos >= 0 ? '+' : '' }}{{ formatPts(m.puntos) }}
                  </span>
                </div>
              </div>
            }
          } @else {
            <div class="empty">Todavía no tenés movimientos de puntos.</div>
          }
        }
      </div>

      <!-- Premios -->
      <div class="card">
        <h2>Premios</h2>
        @if (cargandoPremios) {
          <div class="loading">Cargando premios…</div>
        } @else if (premios.length) {
          @if (canjeMsg) { <div class="canje-ok">{{ canjeMsg }}</div> }
          @if (canjeErr) { <div class="canje-err">{{ canjeErr }}</div> }
          @for (p of premios; track p.id) {
            <div class="row">
              <div class="info">
                <h3>{{ p.nombre }}</h3>
                @if (p.descripcion) { <div class="d">{{ p.descripcion }}</div> }
                <div class="when">{{ formatPts(p.puntosCosto) }} pts</div>
              </div>
              <div class="right">
                <button class="canjear" type="button"
                        [disabled]="saldo < p.puntosCosto || canjeandoId === p.id"
                        (click)="canjear(p)">
                  {{ canjeandoId === p.id ? 'Canjeando…' : (saldo < p.puntosCosto ? 'Puntos insuficientes' : 'Canjear') }}
                </button>
              </div>
            </div>
          }
        } @else {
          <div class="empty">Todavía no hay premios disponibles.</div>
        }
      </div>

      <div class="cols">
      <!-- Mis Citas -->
      <div class="card">
        <h2>Mis Citas</h2>
        @if (cargandoCitas) {
          <div class="loading">Cargando tus citas…</div>
        } @else if (citas.length) {
          @for (c of citas; track c.origen + '-' + c.idRef) {
            <div class="row">
              <div class="info">
                <div class="biz">
                  <h3>{{ c.empresa || c.servicio || 'Cita' }}</h3>
                  @if (c.rubro) { <span class="chip rubro">{{ c.rubro }}</span> }
                </div>
                @if (c.empresa && c.servicio) { <div class="d">{{ c.servicio }}</div> }
                @if (c.sede) { <div class="d">📍 {{ c.sede }}</div> }
                <div class="when">📅 {{ c.fecha }}@if (c.hora) { · 🕒 {{ c.hora }} }</div>
              </div>
              <div class="right">
                <span class="chip" [class.marketplace]="c.origen === 'marketplace'" [class.clinica]="c.origen === 'clinica'">
                  {{ c.origen === 'marketplace' ? 'Marketplace' : 'Clínica' }}
                </span>
                @if (c.estado) { <span class="estado">{{ c.estado }}</span> }
              </div>
            </div>
          }
        } @else {
          <div class="empty">Todavía no tenés citas registradas. Cuando reserves, van a aparecer acá.</div>
        }
      </div>

      <!-- Recomendaciones -->
      <div class="card">
        <h2>Recomendaciones de tu especialista</h2>
        @if (cargandoReco) {
          <div class="loading">Cargando recomendaciones…</div>
        } @else if (recomendaciones.length) {
          @for (r of recomendaciones; track $index) {
            <div class="row">
              <div class="info">
                @if (r.empresa || r.zona) {
                  <div class="biz">
                    <h3>{{ r.empresa || r.zona }}</h3>
                    @if (r.rubro) { <span class="chip rubro">{{ r.rubro }}</span> }
                  </div>
                }
                @if (r.empresa && r.zona) { <div class="d">{{ r.zona }}</div> }
                @if (r.comentarioCliente) { <div class="quote">{{ r.comentarioCliente }}</div> }
                @if (r.comentario) { <div class="quote doc">{{ r.comentario }}</div> }
                @if (!r.comentarioCliente && !r.comentario) {
                  <div class="d">Sin comentario.</div>
                }
                <div class="when">
                  @if (r.fecha) { 📅 {{ r.fecha }} }
                  @if (r.sesion != null) { · Sesión {{ r.sesion }} }
                </div>
              </div>
            </div>
          }
        } @else {
          <div class="empty">Tu especialista todavía no dejó recomendaciones. Aparecerán después de tus tratamientos.</div>
        }
      </div>
      </div>
    </div>
  } @else {
    <div class="gate">
      <div class="card">
        <h2>Iniciá sesión para ver tu cuenta</h2>
        <p>Accedé a tus citas y a las recomendaciones que dejó tu especialista.</p>
        <button class="btn" type="button" (click)="pedirLogin()">Iniciar sesión</button>
      </div>
    </div>
  }
  `,
})
export class MiCuentaComponent implements OnInit {
  citas: MiCita[] = [];
  recomendaciones: MiRecomendacion[] = [];
  saldo = 0;
  movimientos: MovimientoPunto[] = [];
  premios: Premio[] = [];
  cargandoCitas = false;
  cargandoReco = false;
  cargandoPuntos = false;
  cargandoPremios = false;
  canjeandoId: number | null = null;
  canjeMsg = '';
  canjeErr = '';

  constructor(
    private api: MarketplaceApiService,
    public auth: MarketplaceAuthService,
    private authModal: AuthModalService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    if (this.auth.isLogged()) {
      this.cargar();
    } else {
      // Sin sesión: abrimos el modal y cargamos apenas el cliente se autentica.
      this.authModal.require('login').then((ok) => {
        if (ok) this.cargar();
      });
    }
  }

  pedirLogin(): void {
    this.authModal.require('login').then((ok) => {
      if (ok) this.cargar();
    });
  }

  private cargar(): void {
    this.cdr.detectChanges();
    this.cargarPuntos();
    this.cargarPremios();
    this.cargarCitas();
    this.cargarRecomendaciones();
  }

  /** Formatea puntos con separador de miles (ej. 1240 → "1.240"), preservando el signo. */
  formatPts(n: number): string {
    const abs = Math.abs(n ?? 0).toLocaleString('es-PE');
    return n < 0 ? `-${abs}` : abs;
  }

  private cargarPuntos(): void {
    this.cargandoPuntos = true;
    this.cdr.detectChanges();
    this.api.getPuntos().subscribe({
      next: (r) => {
        this.saldo = r?.saldo ?? 0;
        this.movimientos = r?.movimientos ?? [];
        this.cargandoPuntos = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.saldo = 0;
        this.movimientos = [];
        this.cargandoPuntos = false;
        this.cdr.detectChanges();
      },
    });
  }

  private cargarPremios(): void {
    this.cargandoPremios = true;
    this.cdr.detectChanges();
    this.api.getPremios().subscribe({
      next: (r) => {
        this.premios = r?.premios ?? [];
        this.cargandoPremios = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.premios = [];
        this.cargandoPremios = false;
        this.cdr.detectChanges();
      },
    });
  }

  canjear(p: Premio): void {
    if (this.saldo < p.puntosCosto || this.canjeandoId != null) return;
    this.canjeandoId = p.id;
    this.canjeMsg = '';
    this.canjeErr = '';
    this.cdr.detectChanges();
    this.api.canjear(p.id).subscribe({
      next: (r) => {
        this.saldo = r?.saldo ?? this.saldo;
        this.canjeMsg = `¡Canjeaste "${r?.premio?.nombre ?? p.nombre}"! Nuevo saldo: ${this.formatPts(this.saldo)} pts.`;
        this.canjeandoId = null;
        this.cdr.detectChanges();
        // Refresca los movimientos para reflejar el canje.
        this.cargarPuntos();
      },
      error: (e) => {
        this.canjeErr = e?.error?.message ?? 'No se pudo canjear el premio.';
        this.canjeandoId = null;
        this.cdr.detectChanges();
      },
    });
  }

  private cargarCitas(): void {
    this.cargandoCitas = true;
    this.cdr.detectChanges();
    this.api.getMisCitas().subscribe({
      next: (r) => {
        this.citas = r?.items ?? [];
        this.cargandoCitas = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.citas = [];
        this.cargandoCitas = false;
        this.cdr.detectChanges();
      },
    });
  }

  private cargarRecomendaciones(): void {
    this.cargandoReco = true;
    this.cdr.detectChanges();
    this.api.getMisRecomendaciones().subscribe({
      next: (r) => {
        this.recomendaciones = r?.items ?? [];
        this.cargandoReco = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.recomendaciones = [];
        this.cargandoReco = false;
        this.cdr.detectChanges();
      },
    });
  }
}
